import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";

const SPORTS_LABEL = {
  tennis:       "테니스",
  soccer:       "축구",
  futsal:       "풋살",
  badminton:    "배드민턴",
  basketball:   "농구",
  bowling:      "볼링",
  table_tennis: "탁구",
  baseball:     "야구",
  golf:         "골프",
  pickleball:   "피클볼",
  billiards:    "당구",
};

const SPORTS_ICON = {
  tennis: "🎾", soccer: "⚽", futsal: "🥅", badminton: "🏸",
  basketball: "🏀", bowling: "🎳", table_tennis: "🏓", baseball: "⚾",
  golf: "⛳", pickleball: "🏸", billiards: "🎱",
};

function formatSportDetail(sportsType, cd) {
  if (!cd) return null;
  switch (sportsType) {
    case "tennis":       return cd.ntrp ? `NTRP ${cd.ntrp}` : null;
    case "soccer":
    case "futsal":       return cd.position || null;
    case "badminton":    return cd.level || null;
    case "basketball":   return [cd.position, cd.level].filter(Boolean).join(" · ") || null;
    case "bowling":      return cd.avg ? `AVG ${cd.avg}` : null;
    case "table_tennis": return cd.level || null;
    case "baseball":     return cd.position || null;
    case "golf":         return cd.handicap ? `핸디캡 ${cd.handicap}` : null;
    case "pickleball":
    case "billiards":    return cd.years ? `구력 ${cd.years}년` : null;
    default:             return null;
  }
}

export default function MyProfilePage({ onBack, onProfileEdit }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [sports, setSports]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    Promise.all([
      supabase.from("user_profiles").select("*").eq("id", user.id).maybeSingle(),
      supabase.from("user_sports").select("*").eq("user_id", user.id).order("created_at", { ascending: true }),
    ]).then(([{ data: p }, { data: s }]) => {
      setProfile(p || null);
      setSports(s || []);
      setLoading(false);
    });
  }, [user?.id]);

  const handleViewSport = (sportsType) => {
    localStorage.setItem("profileEditSport", sportsType);
    onProfileEdit();
  };

  const initials = profile?.nickname
    ? profile.nickname[0].toUpperCase()
    : (user?.email?.[0] || "?").toUpperCase();

  if (loading) return (
    <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F5F5F7" }}>
      <style>{`@keyframes mpSpin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid #f0f0f0", borderTop: "3px solid #E8412A", animation: "mpSpin 0.8s linear infinite" }} />
    </div>
  );

  return (
    <div style={s.page}>
      {/* 헤더 */}
      <div style={s.header}>
        <button style={s.backBtn} onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span style={s.headerTitle}>내 프로필</span>
        <div style={{ width: 36 }} />
      </div>

      <div style={s.body}>
        {/* 프로필 카드 */}
        <div style={s.profileCard}>
          <div style={s.avatarWrap}>
            {profile?.profile_image
              ? <img src={profile.profile_image} alt="profile" style={s.avatarImg} />
              : <div style={s.avatarFallback}><span style={s.avatarInitials}>{initials}</span></div>
            }
          </div>
          <div style={s.profileMeta}>
            <span style={s.nicknameText}>{profile?.nickname || "(닉네임 없음)"}</span>
            {profile?.region && (
              <>
                <span style={s.metaDot}>·</span>
                <span style={s.regionText}>{profile.region}</span>
              </>
            )}
          </div>
          {profile?.awards && <div style={s.awardsText}>🏆 {profile.awards}</div>}
        </div>

        {/* 종목 프로필 */}
        <div style={s.sectionTitle}>등록된 종목 프로필</div>

        {sports.length === 0 ? (
          <div style={s.emptyCard}>
            <div style={s.emptyText}>등록된 종목 프로필이 없어요</div>
          </div>
        ) : (
          sports.map(sp => {
            const detail  = formatSportDetail(sp.sports_type, sp.career_detail);
            const suffix  = sp.is_player ? "선출" : "비선출";
            const fullDetail = [detail, suffix].filter(Boolean).join(" · ");
            const icon    = SPORTS_ICON[sp.sports_type] || "🏅";
            const label   = SPORTS_LABEL[sp.sports_type] || sp.sports_type;
            const hovered = hoveredId === sp.id;
            return (
              <div
                key={sp.id}
                style={{ ...s.sportCard, background: hovered ? "#F5F5F7" : "#fff" }}
                onClick={() => handleViewSport(sp.sports_type)}
                onMouseEnter={() => setHoveredId(sp.id)}
                onMouseLeave={() => setHoveredId(null)}
                onTouchStart={() => setHoveredId(sp.id)}
                onTouchEnd={() => setHoveredId(null)}
              >
                <div style={s.sportLeft}>
                  <span style={s.sportIcon}>{icon}</span>
                  <div>
                    <div style={s.sportLabel}>{label}</div>
                    {fullDetail && <div style={s.sportDetail}>{fullDetail}</div>}
                  </div>
                </div>
                <span style={s.viewHint}>보기 &gt;</span>
              </div>
            );
          })
        )}

        <button
          style={s.addSportBtn}
          onClick={() => {
            localStorage.removeItem("profileEditSport");
            onProfileEdit();
          }}>
          종목 프로필 추가하기
        </button>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100dvh", background: "#F5F5F7",
    fontFamily: "'Noto Sans KR', sans-serif",
    maxWidth: 480, margin: "0 auto",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "calc(env(safe-area-inset-top) + 14px) 16px 14px",
    background: "#fff", borderBottom: "1px solid #f0f0f0",
    position: "sticky", top: 0, zIndex: 10,
  },
  backBtn: {
    background: "none", border: "none", cursor: "pointer",
    padding: 8, color: "#1A1A2E", display: "flex", alignItems: "center",
  },
  headerTitle: { fontSize: 17, fontWeight: 700, color: "#111827" },
  body: { padding: "16px 16px 80px" },

  /* 프로필 카드 */
  profileCard: {
    background: "#fff", borderRadius: 14, padding: "16px 18px",
    marginBottom: 20, boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
    display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
  },
  avatarWrap: { flexShrink: 0 },
  avatarFallback: {
    width: 52, height: 52, borderRadius: "50%",
    background: "#E8412A", display: "flex", alignItems: "center", justifyContent: "center",
  },
  avatarImg: { width: 52, height: 52, borderRadius: "50%", objectFit: "cover" },
  avatarInitials: { fontSize: 20, fontWeight: 800, color: "#fff" },
  profileMeta: { display: "flex", alignItems: "center", gap: 0 },
  nicknameText: { fontSize: 17, fontWeight: 800, color: "#111827" },
  metaDot: { fontSize: 15, color: "#d1d5db", margin: "0 6px" },
  regionText: { fontSize: 14, color: "#6b7280" },
  awardsText: {
    width: "100%", fontSize: 12, color: "#d97706", fontWeight: 500,
    paddingLeft: 66,
  },

  /* 섹션 타이틀 */
  sectionTitle: {
    fontSize: 12, fontWeight: 600, color: "#9ca3af",
    marginBottom: 10, paddingLeft: 2, letterSpacing: "0.02em",
  },

  /* 빈 상태 */
  emptyCard: {
    background: "#fff", borderRadius: 14, padding: "24px 20px",
    textAlign: "center", marginBottom: 16,
  },
  emptyText: { fontSize: 14, color: "#9ca3af" },

  /* 종목 카드 */
  sportCard: {
    borderRadius: 14, padding: "14px 16px",
    marginBottom: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    cursor: "pointer", transition: "background 0.15s",
  },
  sportLeft: { display: "flex", alignItems: "center", gap: 12 },
  sportIcon: { fontSize: 26, lineHeight: 1 },
  sportLabel: { fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 2 },
  sportDetail: { fontSize: 13, color: "#6b7280" },
  viewHint: { fontSize: 13, color: "#9ca3af", fontWeight: 500, flexShrink: 0 },

  addSportBtn: {
    marginTop: 8, width: "100%", padding: "14px",
    background: "#fff", color: "#E8412A",
    border: "1.5px solid #E8412A", borderRadius: 10,
    fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
  },
};
